import React, { useState, useEffect } from 'react';
import { accountingApi } from '../../../services/accountingApi';

const typeBadge = (type) => {
  if (!type) return 'acc-badge-gray';
  const t = type.toLowerCase();
  if (t.includes('asset'))     return 'acc-badge-blue';
  if (t.includes('liabil'))    return 'acc-badge-red';
  if (t.includes('equity'))    return 'acc-badge-green';
  if (t.includes('revenue') || t.includes('income')) return 'acc-badge-blue';
  if (t.includes('expense'))   return 'acc-badge-yellow';
  return 'acc-badge-gray';
};

const fmt = (n) => {
  const num = parseFloat(n) || 0;
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2 });
};

const ChartOfAccounts = () => {
  const [accounts, setAccounts]       = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState('All');
  const [ledger, setLedger]           = useState(null);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    Promise.all([accountingApi.getAccounts(), accountingApi.getAccountTypes()])
      .then(([accsRes, typesRes]) => {
        const toArr = r => Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setAccounts(toArr(accsRes));
        setAccountTypes(toArr(typesRes));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const typeNames = ['All', ...accountTypes.map(t => t.name || t.account_type || t.type || String(t.id || '')).filter(Boolean)];

  const filtered = accounts.filter(a => {
    const typeName = a.account_type?.name || a.type || '';
    const matchType = filterType === 'All' || typeName === filterType;
    const matchSearch = (a.name || '').toLowerCase().includes(search.toLowerCase()) ||
                        (a.gl_code || a.code || '').toString().includes(search);
    return matchType && matchSearch;
  });

  const openLedger = async (account) => {
    setLedgerAccount(account);
    setLedgerLoading(true);
    setLedger(null);
    try {
      const res = await accountingApi.getAccountLedger(account.id);
      setLedger(res.data || res || []);
    } catch (e) {
      setLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading accounts...</div>;
  if (error)   return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {ledger !== null ? (
        <>
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={() => { setLedger(null); setLedgerAccount(null); }}
              className="acc-btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.875rem' }}>
              ← Back
            </button>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>
              Ledger: {ledgerAccount?.name}
            </h3>
          </div>
          <div className="table-content-card">
            {ledgerLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--acc-text-muted)' }}>Loading ledger...</div>
            ) : (
              <table className="coa-table" style={{ width: '100%' }}>
                <thead>
                  <tr><th>Date</th><th>Description</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th><th style={{ textAlign: 'right' }}>Balance</th></tr>
                </thead>
                <tbody>
                  {(ledger.length === 0) && (
                    <tr><td colSpan={5}><div className="acc-empty"><div className="acc-empty-icon">📒</div><h4>No ledger entries</h4></div></td></tr>
                  )}
                  {ledger.map((row, i) => (
                    <tr key={i}>
                      <td>{row.date || row.created_at?.slice(0, 10)}</td>
                      <td>{row.description || row.narration || '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--acc-success)' }}>{row.debit ? fmt(row.debit) : '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--acc-danger)' }}>{row.credit ? fmt(row.credit) : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(row.balance ?? row.running_balance ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="table-content-card">
          <div className="coa-list-fullwidth">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h3 className="acc-card-title" style={{ margin: 0 }}>Account List</h3>
              
              <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--acc-surface)', padding: '0.2rem 0.5rem', borderRadius: 'var(--acc-radius)', border: '1px solid var(--acc-border)', boxShadow: 'var(--acc-shadow-sm)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--acc-text-muted)', marginLeft: '0.5rem' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  className="tabs-search-input"
                  placeholder="Search accounts..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ border: 'none', outline: 'none', padding: '0.4rem', fontSize: '0.9rem', background: 'transparent', minWidth: '180px', color: 'var(--acc-text-main)' }}
                />
                <div style={{ width: '1px', height: '24px', background: 'var(--acc-border)', margin: '0 0.25rem' }}></div>
                <select
                  value={filterType}
                  onChange={e => setFilterType(e.target.value)}
                  style={{ minWidth: '130px', height: '34px', padding: '0 0.5rem', fontSize: '0.875rem', border: 'none', background: 'transparent', outline: 'none', color: 'var(--acc-text-main)', cursor: 'pointer' }}
                >
                  {typeNames.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <table className="coa-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account Name</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                  <th style={{ textAlign: 'center' }}>Ledger</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{a.gl_code || a.code || '—'}</span></td>
                    <td style={{ fontWeight: 500 }}>{a.name}</td>
                    <td><span className={`acc-badge ${typeBadge(a.account_type?.name || a.type || '')}`}>{a.account_type?.name || a.type || '—'}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(a.current_balance ?? a.balance ?? 0)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button onClick={() => openLedger(a)}
                        style={{ background: 'var(--acc-primary-light)', border: 'none', color: 'var(--acc-primary)', borderRadius: '6px', padding: '0.35rem 0.8rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--acc-primary)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--acc-primary-light)'; e.currentTarget.style.color = 'var(--acc-primary)'; }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5}><div className="acc-empty"><div className="acc-empty-icon">📂</div><h4>No accounts found</h4><p>Try adjusting your search criteria</p></div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccounts;
